import React from 'react'
import { MerchantLocation } from '@slerp/controls'
import { Select, Switch } from 'antd'
import {
  TableWrapper,
  TableHeaderBar,
  StoreRow,
  StoreColumn,
  ShopColumn,
  ToggleColumn,
  ColumnDivider,
  StoreName,
  StoreSubtext
} from './styles'
import { formatStoreAddress } from './utils'
import { PUSH_ORDERS_COLUMN_HEADER } from './copy'
import { ConnectStoresRow, LeatStore } from './types'

// List-view only (see allowNone below). Antd's Select can't show a selected
// option for value `null`/`undefined` (renders the placeholder instead), so
// this sentinel distinguishes "explicitly cleared" from "nothing chosen yet".
const NONE_OPTION = '__none__'

interface StoreLinkTableProps {
  stores: MerchantLocation[]
  rows: Record<string, ConnectStoresRow>
  leatShops: LeatStore[]
  loading?: boolean
  allowNone?: boolean
  onRowChange: (storeId: string, patch: Partial<ConnectStoresRow>) => void
}

// `allowNone` is the only difference between the two screens that render this.
const StoreLinkTable = ({
  stores,
  rows,
  leatShops,
  loading,
  allowNone,
  onRowChange
}: StoreLinkTableProps) => (
  <TableWrapper>
    <TableHeaderBar>
      <ToggleColumn>{PUSH_ORDERS_COLUMN_HEADER}</ToggleColumn>
      <ColumnDivider $header />
      <StoreColumn>Slerp Store</StoreColumn>
      <ColumnDivider $header />
      <ShopColumn>Leat Shop</ShopColumn>
    </TableHeaderBar>
    {stores.map((store) => {
      const row = rows[store.id]
      return (
        <StoreRow key={store.id}>
          <ToggleColumn>
            <Switch
              disabled={loading}
              checked={row.leatOrderPushEnabled}
              onChange={(checked) =>
                onRowChange(store.id, { leatOrderPushEnabled: checked })
              }
            />
          </ToggleColumn>
          <ColumnDivider />
          <StoreColumn>
            <StoreName>{store.name}</StoreName>
            <StoreSubtext>{formatStoreAddress(store.address)}</StoreSubtext>
          </StoreColumn>
          <ColumnDivider />
          <ShopColumn>
            <Select
              style={{ width: '100%' }}
              placeholder={allowNone ? undefined : 'Select Shop'}
              disabled={loading}
              value={row.leatStoreId ?? (allowNone ? NONE_OPTION : undefined)}
              onChange={(value: string) =>
                onRowChange(
                  store.id,
                  value === NONE_OPTION
                    ? { leatStoreId: null, leatOrderPushEnabled: false }
                    : // Only restores push-enabled coming from null: swapping
                    // between two real shops must leave a manual override alone.
                    allowNone && row.leatStoreId === null
                    ? { leatStoreId: value, leatOrderPushEnabled: true }
                    : { leatStoreId: value }
                )
              }
              options={[
                ...(allowNone ? [{ value: NONE_OPTION, label: 'None' }] : []),
                ...leatShops.map((shop) => ({
                  value: shop.uuid,
                  label: shop.name
                }))
              ]}
            />
          </ShopColumn>
        </StoreRow>
      )
    })}
  </TableWrapper>
)

export default StoreLinkTable
